import { useEffect, useState, type FC } from "react";
import {
  CircleMarker,
  MapContainer,
  TileLayer,
  Tooltip,
  useMap,
} from "react-leaflet";
import type { Feature } from "../../types";

interface MoonMapProps {
  features: Feature[];
  selectedId: string | null;
  onSelect: (feature: Feature) => void;
}

function FlyTo({ feature }: { feature: Feature | null }) {
  const map = useMap();

  useEffect(() => {
    if (!feature) {
      return;
    }
    map.flyTo([feature.latitude, feature.longitude], Math.max(map.getZoom(), 5), {
      duration: 0.7,
    });
  }, [feature, map]);

  return null;
}

function FitContainer() {
  const map = useMap();

  useEffect(() => {
    const invalidate = () => map.invalidateSize();
    invalidate();
    const timer = window.setTimeout(invalidate, 100);
    window.addEventListener("resize", invalidate);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("resize", invalidate);
    };
  }, [map]);

  return null;
}

function markerRadius(count: number): number {
  return Math.max(6, Math.min(26, 5 + Math.sqrt(count) * 2.4));
}

const MoonMap: FC<MoonMapProps> = ({ features, selectedId, onSelect }) => {
  const [mounted, setMounted] = useState(false);
  const selected = features.find((feature) => feature.id === selectedId) ?? null;

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return <div style={{ width: "100%", height: "100%", background: "#0b1213" }} />;
  }

  return (
    <MapContainer
      center={[0, 0]}
      zoom={3}
      minZoom={1}
      maxZoom={8}
      scrollWheelZoom
      worldCopyJump={false}
      style={{ width: "100%", height: "100%", background: "#0b1213" }}
    >
      <TileLayer
        attribution='Moon basemap © <a href="https://www.openplanetary.org/">OpenPlanetaryMap</a> · NASA LRO / USGS'
        url="https://cartocdn-gusc.global.ssl.fastly.net/opmbuilder/api/v1/map/named/opm-moon-basemap-v0-1/all/{z}/{x}/{y}.png"
        maxNativeZoom={6}
        maxZoom={8}
      />
      {features.map((feature) => {
        const selectedFeature = feature.id === selectedId;
        return (
          <CircleMarker
            key={feature.id}
            center={[feature.latitude, feature.longitude]}
            radius={markerRadius(feature.eventCount) + (selectedFeature ? 3 : 0)}
            pathOptions={{
              color: selectedFeature ? "#FA5468" : "#F6BB51",
              fillColor: selectedFeature ? "#D32A46" : "#E2991A",
              fillOpacity: selectedFeature ? 0.85 : 0.55,
              weight: selectedFeature ? 3 : 1.5,
            }}
            eventHandlers={{
              click: () => onSelect(feature),
            }}
          >
            <Tooltip direction="top" offset={[0, -4]}>
              <span className="font-semibold">{feature.name}</span>
              <span>
                {" "}
                · {feature.eventCount} event{feature.eventCount === 1 ? "" : "s"}
              </span>
            </Tooltip>
          </CircleMarker>
        );
      })}
      <FlyTo feature={selected} />
      <FitContainer />
    </MapContainer>
  );
};

export default MoonMap;
